import { gql } from '@apollo/client';

// Fragment for consistent product fields
export const PRODUCT_FIELDS_FRAGMENT = gql`
  fragment ProductFields on Product {
    id
    title
    handle
    description
    descriptionHtml
    availableForSale
    productType
    vendor
    tags
    options {
      id
      name
      values
    }
    variants(first: 50) {
      edges {
        node {
          id
          title
          availableForSale
          selectedOptions {
            name
            value
          }
          price {
            amount
            currencyCode
          }
          compareAtPrice {
            amount
            currencyCode
          }
          image {
            id
            url
            altText
            width
            height
          }
          metafields(
            identifiers: [
              { namespace: "custom", key: "color_swatch" },
              { namespace: "specifications", key: "color_swatch" }
            ]
          ) {
            id
            namespace
            key
            value
            type
          }
        }
      }
    }
    images(first: 5) {
      edges {
        node {
          id
          url
          altText
          width
          height
        }
      }
    }
    metafields(
      identifiers: [
        { namespace: "custom", key: "specifications" },
        { namespace: "custom", key: "camera" },
        { namespace: "custom", key: "battery" },
        { namespace: "custom", key: "processor" },
        { namespace: "custom", key: "model" },
        { namespace: "custom", key: "sim_card" },
        { namespace: "custom", key: "connector" },
        { namespace: "custom", key: "charging_block_wattage" },
        { namespace: "custom", key: "wireless_charging" },
        { namespace: "custom", key: "front_camera" },
        { namespace: "custom", key: "rear_camera" },
        { namespace: "custom", key: "slow_motion_video" },
        { namespace: "custom", key: "optical_zoom" },
        { namespace: "custom", key: "apple_watch_compatible" },
        { namespace: "custom", key: "apple_watch_se_compatible" },
        { namespace: "custom", key: "apple_watch_ultra_compatible" },
        { namespace: "custom", key: "phone_size" },
        { namespace: "custom", key: "microsd_support" },
        { namespace: "custom", key: "storage" },
        { namespace: "custom", key: "resolution" },
        { namespace: "custom", key: "display" },
        { namespace: "custom", key: "weight" },
        { namespace: "custom", key: "last_os_supported" },
        { namespace: "custom", key: "works_until" },
        { namespace: "custom", key: "release_date" },
        { namespace: "custom", key: "dimensions" },
        { namespace: "custom", key: "camera" },
        { namespace: "custom", key: "processor" },
        { namespace: "custom", key: "3g_4g_5g" },
        { namespace: "custom", key: "headphone_port" },
        { namespace: "custom", key: "navigation" },
        { namespace: "custom", key: "color_swatches" },
        { namespace: "custom", key: "landscape_photo" },
        { namespace: "custom", key: "close_up_photo" },
        { namespace: "custom", key: "low_light_photo" },
        { namespace: "custom", key: "landscape" },
        { namespace: "custom", key: "close_up" },
        { namespace: "custom", key: "low_light" }
      ]
    ) {
      id
      namespace
      key
      value
      type
      reference {
        ... on MediaImage {
          id
          image {
            url
            altText
            width
            height
          }
        }
      }
    }
  }
`;

// Lightweight fragment for initial product loading - only essential fields
export const PRODUCT_FIELDS_LIGHTWEIGHT = gql`
  fragment ProductFieldsLightweight on Product {
    id
    title
    handle
    productType
    vendor
    tags
    options {
      id
      name
      values
    }
    variants(first: 10) {
      edges {
        node {
          id
          title
          availableForSale
          price {
            amount
            currencyCode
          }
          image {
            id
            url
            altText
            width
            height
          }
        }
      }
    }
    images(first: 1) {
      edges {
        node {
          id
          url
          altText
          width
          height
        }
      }
    }
    metafields(
      identifiers: [
        { namespace: "custom", key: "release_date" },
        { namespace: "custom", key: "model" }
      ]
    ) {
      id
      namespace
      key
      value
      type
    }
  }
`;

// Fast query for initial products - minimal data
export const GET_COLLECTION_PRODUCTS_LIGHTWEIGHT = gql`
  query GetCollectionProductsLightweight($handle: String!, $first: Int!) {
    collection(handle: $handle) {
      id
      title
      handle
      products(first: $first) {
        edges {
          cursor
          node {
            ...ProductFieldsLightweight
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
  ${PRODUCT_FIELDS_LIGHTWEIGHT}
`;

// Intermediate fragment - all metafields but only 1 variant per product
export const PRODUCT_FIELDS_INTERMEDIATE = gql`
  fragment ProductFieldsIntermediate on Product {
    id
    title
    handle
    description
    descriptionHtml
    availableForSale
    productType
    vendor
    tags
    options {
      id
      name
      values
    }
    variants(first: 1) {
      edges {
        node {
          id
          title
          availableForSale
          selectedOptions {
            name
            value
          }
          price {
            amount
            currencyCode
          }
          compareAtPrice {
            amount
            currencyCode
          }
          image {
            id
            url
            altText
            width
            height
          }
          metafields(
            identifiers: [
              { namespace: "custom", key: "color_swatch" }
            ]
          ) {
            id
            namespace
            key
            value
            type
          }
        }
      }
    }
    images(first: 5) {
      edges {
        node {
          id
          url
          altText
          width
          height
        }
      }
    }
    metafields(
      identifiers: [
        { namespace: "custom", key: "specifications" },
        { namespace: "custom", key: "camera" },
        { namespace: "custom", key: "battery" },
        { namespace: "custom", key: "processor" },
        { namespace: "custom", key: "model" },
        { namespace: "custom", key: "sim_card" },
        { namespace: "custom", key: "connector" },
        { namespace: "custom", key: "charging_block_wattage" },
        { namespace: "custom", key: "wireless_charging" },
        { namespace: "custom", key: "front_camera" },
        { namespace: "custom", key: "rear_camera" },
        { namespace: "custom", key: "slow_motion_video" },
        { namespace: "custom", key: "optical_zoom" },
        { namespace: "custom", key: "apple_watch_compatible" },
        { namespace: "custom", key: "apple_watch_se_compatible" },
        { namespace: "custom", key: "apple_watch_ultra_compatible" },
        { namespace: "custom", key: "phone_size" },
        { namespace: "custom", key: "microsd_support" },
        { namespace: "custom", key: "storage" },
        { namespace: "custom", key: "resolution" },
        { namespace: "custom", key: "display" },
        { namespace: "custom", key: "weight" },
        { namespace: "custom", key: "last_os_supported" },
        { namespace: "custom", key: "works_until" },
        { namespace: "custom", key: "release_date" },
        { namespace: "custom", key: "dimensions" },
        { namespace: "custom", key: "camera" },
        { namespace: "custom", key: "processor" },
        { namespace: "custom", key: "3g_4g_5g" },
        { namespace: "custom", key: "headphone_port" },
        { namespace: "custom", key: "navigation" },
        { namespace: "custom", key: "color_swatches" },
        { namespace: "custom", key: "landscape_photo" },
        { namespace: "custom", key: "close_up_photo" },
        { namespace: "custom", key: "low_light_photo" },
        { namespace: "custom", key: "landscape" },
        { namespace: "custom", key: "close_up" },
        { namespace: "custom", key: "low_light" }
      ]
    ) {
      id
      namespace
      key
      value
      type
      reference {
        ... on MediaImage {
          id
          image {
            url
            altText
            width
            height
          }
        }
      }
    }
  }
`;

// Stage 2 query - all products with 1 variant each
export const GET_COLLECTION_PRODUCTS_INTERMEDIATE = gql`
  query GetCollectionProductsIntermediate($handle: String!, $first: Int!) {
    collection(handle: $handle) {
      id
      title
      handle
      products(first: $first) {
        edges {
          cursor
          node {
            ...ProductFieldsIntermediate
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
  ${PRODUCT_FIELDS_INTERMEDIATE}
`;

// Stage 3 query - full product data with all variants
export const GET_COLLECTION_PRODUCTS = gql`
  query GetCollectionProducts($handle: String!, $first: Int!) {
    collection(handle: $handle) {
      id
      title
      handle
      products(first: $first) {
        edges {
          cursor
          node {
            ...ProductFields
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
  ${PRODUCT_FIELDS_FRAGMENT}
`;

// Fallback query to get all products
export const GET_ALL_PRODUCTS = gql`
  query GetAllProducts($first: Int!) {
    products(first: $first) {
      edges {
        node {
          ...ProductFields
        }
      }
    }
  }
  ${PRODUCT_FIELDS_FRAGMENT}
`;

// Query to get specific products by their IDs
export const GET_PRODUCTS_BY_IDS = gql`
  query GetProductsByIds($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on Product {
        ...ProductFields
      }
    }
  }
  ${PRODUCT_FIELDS_FRAGMENT}
`;